import { Injectable } from '@angular/core'
import { AngularFirestore } from '@angular/fire/compat/firestore'
import { BehaviorSubject, map, Subscription, combineLatest } from 'rxjs'
import { ToastService } from 'src/app/services/toast.service'
import { UserService } from '../../_shared/services/user.service'
import { Router } from '@angular/router'
import { BasketData, BasketDocument, BasketItem } from 'tolaria-cloud-functions/src/_interfaces'
import { IExchangeRate, PaymentService } from 'src/app/payment/payment.service'
import { ProductListingService } from './product-listing.service'
import { v4 as uuidv4 } from 'uuid'
import { MessageModalService } from 'src/app/components/modals/message-modal/message-modal.service'

@Injectable({ providedIn: 'root' })
export class BasketService {

  private items$ = new BehaviorSubject<BasketItem[]>([])
  private basketDocSubscription: Subscription
  private basketData$ = new BehaviorSubject<BasketData>(null)
  private _hasMultipleCurrencies$ = new BehaviorSubject<boolean>(false);
  public hasMultipleCurrencies$ = this._hasMultipleCurrencies$.asObservable();
  private _basketTotal$ = new BehaviorSubject<number>(0);
  public basketTotal$ = this._basketTotal$.asObservable();
  private _basketCurrency$ = new BehaviorSubject<string | null>(null);
  public basketCurrency$ = this._basketCurrency$.asObservable();
  private _currencyExchanges$ = new BehaviorSubject<string[]>([]);
  public currencyExchanges$ = this._currencyExchanges$.asObservable();
  

  constructor(
    private readonly toast: ToastService,
    private readonly firestore: AngularFirestore,
    private readonly user: UserService,
    private readonly router: Router,
    private readonly paymentService: PaymentService,
    private readonly productListing: ProductListingService,
    private readonly confirm: MessageModalService,
  ) {
    console.log('BasketService:: construction')
    combineLatest([this.user.documentsLoaded$, this.paymentService.ready$]).subscribe(([loaded, ready]) => {
      if (loaded && ready) {
        this.initUserBasket()
      }
      else {
        this.clearUserBasket()
      }
    })
    combineLatest([this.items$, this.paymentService.exchangeRates$]).subscribe(([items, rates]) => {
      this.calculateBasketTotals(items, rates);
    })
  }

  private initUserBasket(): void {
    this.basketData$.next({
      basketId: uuidv4(),
      playerId: this.user.getPlayerId(),
      userId: this.user.getUid(),
      items: [],
      customerId: this.user.stripeAccountId,
    })
    const docObserver = this.firestore
      .collection('marketplace')
      .doc('store')
      .collection('basket')
      .doc<BasketDocument>(this.user.getUid())
      .valueChanges()
    this.basketDocSubscription = docObserver.subscribe(doc => {
      console.log('BasketService:: user basket document emitted')
      const items = doc && doc.items ? this.mapBasketItems(doc) : []
      const data = doc
        ? {
            ...doc,
            customerId: this.user.stripeAccountId,
          }
        : {
          basketId: uuidv4(),
          playerId: this.user.getPlayerId(),
          userId: this.user.getUid(),
          items: items,
          customerId: this.user.stripeAccountId,
        }
      this.basketData$.next(data)
      this.items$.next(items)
    })
  }

  private mapBasketItems(basket: BasketDocument): BasketItem[] {
    return basket.items.map(item => {
      let tmp = {
        ...item
      }
      if ([...new Set(basket.items.map(i => i.currency))].length > 1 && item.currency !== 'EUR') {
        tmp.converted = {
          price: Math.round((item.price / this.paymentService.exchangeRates$.getValue()[item.currency]) * item.quantity),
          currency: 'EUR',
          rate: this.paymentService.exchangeRates$.getValue()[item.currency],
        }
      }
      return tmp
    })
  }

  private clearUserBasket(): void {
    this.items$.next([])
    this.basketData$.next(null)
    this.basketDocSubscription?.unsubscribe()
  }

  public get basket$() {
    return this.basketData$.asObservable()
  }

  public get basketItems$() {
    return this.items$.asObservable()
  }

  public get totalItems$() {
    return this.items$.asObservable().pipe(
      map(items => items.reduce((sum, item) => sum + item.quantity, 0))
    )
  }

  public addItem(item: BasketItem): void {
    const current = this.basketData$.value
    const existing = current.items.find(i => i.id === item.id)

    let updated = []
    let isUpdate = false
    if (existing) {
      updated = current.items.map(i =>
        i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i
      )
      isUpdate = true
    }
    else {
      updated = [...current.items, item]
    }

    current.items = updated

    this.updateBasketDocument(current, isUpdate ? 'updated' : 'item-added')

  }

  public removeItem(id: string): void {
    const current = this.basketData$.value
    const updated = this.items$.value.filter(i => i.id !== id)
    current.items = updated
    this.updateBasketDocument(current, 'item-removed')
  }

  public clear(): void {
    // first, make sure the user wants to clear the basket
    this.confirm.open({
      message: 'Are you sure you want to clear the basket?',
      buttons: [
        {
          text: 'Cancel',
          type: 'dismiss',
          value: 'dismissed',
        },
        {
          text: 'Clear',
          type: 'close',
          value: 'clear-basket',
        },
      ],
      title: 'Clear basket?',
      type: 'warning',
    })
    .then((response) => {
      if (response === 'clear-basket') {
        const current: BasketData = this.basketData$.value
        // remove all items
        current.items = []
        // create a new basketId
        current.basketId = uuidv4()
        // update the basket document
        this.updateBasketDocument(current, 'cleared')
        // clear the local item subject
        this.items$.next([])
      }
    })
  }

  public getCurrentItems(): BasketItem[] {
    return this.items$.value
  }

  private updateBasketDocument(document: BasketDocument, operation: 'updated' | 'item-added' | 'item-removed' | 'cleared' = 'updated'): void {
    this.firestore
      .collection('marketplace')
      .doc('store')
      .collection('basket')
      .doc(this.user.getUid())
      .set(document)
      .then(() => {
        switch (operation) {
          case 'updated':
            if (!this.router.url.includes('basket')) {
              this.toast.show('Basket updated', { classname: 'bg-success text-light' })
            }
            break
          case 'item-added':
            if (!this.router.url.includes('basket')) {
              this.toast.show('Item added to your basket', { classname: 'bg-success text-light' })
            }
            break
          case 'item-removed':
            if (!this.router.url.includes('basket')) {
              this.toast.show('Item removed from your basket', { classname: 'bg-success text-light' })
            }
            break
          case 'cleared':
            if (!this.router.url.includes('basket')) {
              this.toast.show('Basket cleared', { classname: 'bg-success text-light' })
            }
            this.router.navigate(['/marketplace', 'products'])
            break
        }
      })
  }

  public getBasketData(): BasketData {
    return this.basketData$.getValue()
  }

  public getCheckoutData(): BasketData {
    const basket = this.getBasketData()
    const items = basket.items.map(item => {
      const product = this.productListing.getProductById(item.productId)
      let tmp = {
        ...item
      }
      tmp.name = product.hasVariants
        ? tmp.name + ', ' + product.variants.find(i => i.id === item.variantId).attributes.map(i => i.value).join(' - ')
        : tmp.name
      return tmp
    })
    const checkoutData: BasketData = {
      ...basket
    }
    checkoutData.items = items
    return checkoutData
  }
  
  private calculateBasketTotals(items: BasketItem[], rates: IExchangeRate[]): void {
    const currencies = new Set(items.map(item => item.currency));
    this._hasMultipleCurrencies$.next(currencies.size > 1);

    if (items.length === 0) {
      this._basketTotal$.next(0);
      this._basketCurrency$.next(null);
      this._currencyExchanges$.next([]);
      return;
    }

    let totalEUR = 0;
    let totalSingleCurrency = 0;
    let singleCurrency: string | null = null;

    if (this._hasMultipleCurrencies$.value) {
      items.forEach(item => {
        const exchangeRate = rates[item.currency];
        totalEUR += (item.price / exchangeRate) * item.quantity;
      });
      this._basketTotal$.next(totalEUR);
      this._basketCurrency$.next('EUR');
    } else {
      singleCurrency = items[0].currency;
      totalSingleCurrency = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      this._basketTotal$.next(totalSingleCurrency);
      this._basketCurrency$.next(singleCurrency);
    }

    const currencyExchanges = [...currencies].filter(i => i !== 'EUR').map(currency => {
      const rate = rates[currency];
      return `${currency}: ${rate.toFixed(2)}`;
    });
    this._currencyExchanges$.next(currencyExchanges);
  }

}
